import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, ArrowLeft } from "lucide-react";
import PublicLayout from "@/layouts/PublicLayout";
import ServiceCard from "@/components/marketplace/ServiceCard";
import ProviderCard from "@/components/marketplace/ProviderCard";
import TemplateBands from "@/components/marketplace/TemplateBands";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import api from "@/lib/api";

export default function LocationPage() {
  const { state, city } = useParams();
  const [stateDoc, setStateDoc] = useState(null);
  const [cityDoc, setCityDoc] = useState(null);
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [notFound, setNotFound] = useState(false);
  useDocumentTitle(cityDoc?.name || stateDoc?.name || (notFound ? "Location not found" : "Location"));

  useEffect(() => {
    (async () => {
      try {
        const stateSlug = state; // e.g. "florida"
        const { data: st } = await api.get(`/locations/${stateSlug}`);
        setStateDoc(st);
        let target = st;
        if (city) {
          const { data: c } = await api.get(`/locations/${city}`);
          setCityDoc(c);
          target = c;
        }
        const [s, p] = await Promise.all([
          api.get("/services", { params: { location_id: target.id, limit: 24 } }),
          api.get("/providers", { params: { location_id: target.id, limit: 12 } }),
        ]);
        setServices(s.data.items || []);
        setProviders(p.data.items || []);
      } catch (_) {
        setNotFound(true);
      }
    })();
  }, [state, city]);

  if (notFound) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h1 className="font-display text-3xl text-[#071b22]">Location not found.</h1>
          <Link to="/services" className="mt-6 inline-flex items-center gap-2 text-[#165a71] font-medium"><ArrowLeft className="h-4 w-4" /> Back to services</Link>
        </div>
      </PublicLayout>
    );
  }
  if (!stateDoc) return <PublicLayout><div className="mx-auto max-w-3xl px-6 py-24 text-[#252A34]/60">Loading…</div></PublicLayout>;

  const location = cityDoc || stateDoc;

  return (
    <PublicLayout>
      <TemplateBands entity="locations" slot="header" />
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12">
        <div className="flex items-center gap-2 text-xs text-[#252A34]/60 mb-3">
          <Link to="/services" className="hover:text-[#165a71]">Services</Link>
          <span>/</span>
          <Link to={`/locations/${stateDoc.slug}`} className="hover:text-[#165a71]">{stateDoc.name}</Link>
          {cityDoc && (<><span>/</span><span>{cityDoc.name}</span></>)}
        </div>
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#165a71]/10 text-[#165a71] flex items-center justify-center">
            <MapPin className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-4xl md:text-5xl font-semibold text-[#071b22] tracking-tight" data-testid="location-title">
              {location.name}
            </h1>
            <div className="mt-1 text-[#252A34]/70">
              {stateDoc.state_code} · {cityDoc ? "City" : "State"} on the Gulf Coast
            </div>
          </div>
        </div>

        <section className="mt-12">
          <h2 className="font-display text-2xl text-[#071b22] font-semibold mb-6">Services in {location.name}</h2>
          {services.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center bg-white" data-testid="location-services-empty">
              <div className="text-[#252A34]/70">No published services here yet — check nearby cities.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="location-services">
              {services.map((s, i) => (
                <ServiceCard key={s.id} service={s} index={i} />
              ))}
            </div>
          )}
        </section>

        {providers.length > 0 && (
          <section className="mt-16">
            <h2 className="font-display text-2xl text-[#071b22] font-semibold mb-6">Providers in {location.name}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="location-providers">
              {providers.map((p, i) => (
                <ProviderCard key={p.id} provider={p} index={i} />
              ))}
            </div>
          </section>
        )}
      </div>
      <TemplateBands entity="locations" slot="footer" />
    </PublicLayout>
  );
}
